import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  loadTodayRank, loadWeeklyRank, loadMonthlyRank, loadClubAttend, searchClubMember,
} from '../../../lib/api/runningtrackMonitor';
import {
  Card, CardHead, CardTitle, CardBody, Tools, Hint, TableWrap, Tbl, Empty, SegButtons, SmallButton, RangeText, Who,
} from './ZetaUi';
import ZetaMemberModal from './ZetaMemberModal';
import {
  presetRange, thisWeekRange, rangeUnix, rangeLabel, dayKey, fromUnix, km, dur, num, maskPhone, mapLimit, getMemberInfo,
} from './zetaUtil';

// 기록한 회원 — 프론트만으로 되는 범위.
//   명단 = 랭킹 API(오늘·이번 주·이번 달, 거리 상위 50명). 러닝 이용일 = 주간·월간 랭킹의 운동일수.
//   출석일수 = 같은 기간 출입문 기록. 아이디·닉네임 = memberinfo. 아무 날짜 지정·전체 명단은 서버 수정 후.

const SearchBox = styled.form`
  display: flex;
  align-items: center;
  gap: 6px;
  input {
    border: 1px solid #e0e4ea; border-radius: 8px; padding: 7px 10px; width: 240px; max-width: 60vw;
    font-family: inherit; font-size: 13px;
  }
`;

const PERIODS = [['today', '오늘'], ['week', '이번 주'], ['month', '이번 달']];
const LOADERS = { today: loadTodayRank, week: loadWeeklyRank, month: loadMonthlyRank };
const rangeOf = (p) => (p === 'today' ? presetRange('today') : p === 'week' ? thisWeekRange() : presetRange('month'));

const ZetaMembers = ({ clubID, socketKey }) => {
  const [period, setPeriod] = useState('week');
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [doorDays, setDoorDays] = useState(null);
  const [infos, setInfos] = useState({});
  const [query, setQuery] = useState('');
  const [found, setFound] = useState(null);
  const [openID, setOpenID] = useState(null);

  useEffect(() => {
    if (!clubID) return;
    let alive = true;
    setRows(null); setDoorDays(null);
    LOADERS[period](clubID)
      .then((res) => {
        const d = res.data || {};
        const body = period === 'today' ? d : d.distance || {};
        const list = (Array.isArray(body.infos) ? body.infos : []).map((x) => ({ ...x, dayCount: period === 'today' ? 1 : Number(x.dayCount) || 0 }));
        if (!alive) return;
        setRows(list); setTotal(body.total || 0);
        mapLimit(list, 6, async (x) => {
          const info = await getMemberInfo(x.memberID, clubID);
          if (alive && info) setInfos((prev) => ({ ...prev, [x.memberID]: info }));
        });
      })
      .catch(() => alive && setRows([]));
    const { startTime, endTime } = rangeUnix(rangeOf(period));
    loadClubAttend(clubID, startTime, endTime, socketKey)
      .then((res) => {
        const seen = {};
        (Array.isArray(res.data) ? res.data : []).forEach((x) => {
          const k = `${x.playerID}_${dayKey(fromUnix(x.attendTime))}`;
          seen[k] = x.playerID;
        });
        const days = {};
        Object.values(seen).forEach((pid) => { days[pid] = (days[pid] || 0) + 1; });
        if (alive) setDoorDays(days);
      })
      .catch(() => alive && setDoorDays({}));
    return () => { alive = false; };
  }, [clubID, socketKey, period]);

  const onSearch = async (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) { setFound(null); return; }
    setFound('loading');
    const results = [];
    try {
      const res = await searchClubMember(clubID, q);
      (res.data && Array.isArray(res.data.infos) ? res.data.infos : []).forEach((m) => results.push(m));
    } catch (err) { /* 검색 실패는 빈 결과로 */ }
    if (/^\d+$/.test(q) && !results.some((m) => String(m.memberID) === q)) {
      const info = await getMemberInfo(Number(q), clubID);
      if (info) results.unshift(info);
    }
    setFound(results);
  };

  const q = query.trim().toLowerCase();
  const matches = (x) => {
    const info = infos[x.memberID] || {};
    return !q || String(x.name || '').toLowerCase().includes(q) || String(info.account || '').toLowerCase().includes(q) || String(x.memberID) === q;
  };
  const shown = (rows || []).filter(matches);

  return (
    <Card>
      <CardHead>
        <CardTitle><span role="img" aria-label="회원">👤</span> 기록한 회원</CardTitle>
        <Tools>
          <SegButtons options={PERIODS} value={period} onChange={setPeriod} />
          <RangeText>{rangeLabel(rangeOf(period))} · 기록 {num(total)}명</RangeText>
        </Tools>
      </CardHead>
      <CardBody>
        <Hint>
          랭킹 명단(거리 상위 50명) 기준으로 오늘·이번 주·이번 달을 봅니다.
          출석일수는 출입문 기록, 러닝 이용일은 그 기간 제타트랙 기록이 있는 날 수입니다. 시간은 참고값입니다.
        </Hint>
        <SearchBox onSubmit={onSearch}>
          <input value={query} onChange={(e) => { setQuery(e.target.value); if (!e.target.value.trim()) setFound(null); }} placeholder="이름·아이디·회원번호·전화 뒤 4자리" aria-label="회원 검색" />
          <SmallButton type="submit">찾기</SmallButton>
        </SearchBox>
        <TableWrap>
          <Tbl>
            <thead>
              <tr><th>이름</th><th>아이디</th><th>회원번호</th><th className="r">출석일수</th><th className="r">러닝 이용일</th><th className="r">평균 거리</th><th className="r">시간 (참고)</th><th /></tr>
            </thead>
            <tbody>
              {shown.map((x) => {
                const info = infos[x.memberID];
                return (
                  <tr key={x.memberID} className="click" onClick={() => setOpenID(x.memberID)}>
                    <td><Who name={x.name} main={x.name} sub={info && info.nickName ? info.nickName : ''} /></td>
                    <td style={{ fontFamily: 'Roboto Mono, monospace', fontSize: 12.5 }}>{info ? info.account || '-' : '…'}</td>
                    <td className="muted">{x.memberID}</td>
                    <td className="r strong">{doorDays ? `${doorDays[x.memberID] || 0}일` : '…'}</td>
                    <td className="r strong">{x.dayCount}일</td>
                    <td className="r">{km(x.dayCount ? Math.round(x.distance / x.dayCount) : x.distance)}</td>
                    <td className="r muted">{dur(x.time)}</td>
                    <td className="muted">상세 ›</td>
                  </tr>
                );
              })}
            </tbody>
          </Tbl>
          {!rows ? <Empty>불러오는 중…</Empty> : !shown.length ? <Empty>{q ? '이 기간 명단에 맞는 회원이 없습니다' : '이 기간에 기록한 회원이 없습니다'}</Empty> : null}
        </TableWrap>
        {rows && total > rows.length ? <Hint style={{ textAlign: 'center' }}>거리 상위 {rows.length}명까지만 표시합니다 (전체 {num(total)}명)</Hint> : null}

        {found ? (
          <>
            <CardTitle as="h3" style={{ fontSize: '1rem' }}>기간과 상관없이 찾은 회원</CardTitle>
            <TableWrap>
              <Tbl>
                <thead><tr><th>이름</th><th>아이디</th><th>회원번호</th><th>전화</th><th /></tr></thead>
                <tbody>
                  {found !== 'loading' && found.map((m) => (
                    <tr key={m.memberID} className="click" onClick={() => setOpenID(m.memberID)}>
                      <td><Who name={m.name} main={m.name} sub={m.nickName || ''} /></td>
                      <td style={{ fontFamily: 'Roboto Mono, monospace', fontSize: 12.5 }}>{m.account || '-'}</td>
                      <td className="muted">{m.memberID}</td>
                      <td className="muted">{maskPhone(m.phone)}</td>
                      <td className="muted">상세 ›</td>
                    </tr>
                  ))}
                </tbody>
              </Tbl>
              {found === 'loading' ? <Empty>찾는 중…</Empty> : !found.length ? <Empty>검색 결과가 없습니다</Empty> : null}
            </TableWrap>
          </>
        ) : null}
      </CardBody>
      <ZetaMemberModal open={!!openID} memberID={openID} clubID={clubID} socketKey={socketKey} onClose={() => setOpenID(null)} />
    </Card>
  );
};

export default ZetaMembers;
